import {
  CloseOutlined,
  InfoCircleOutlined,
  LikeOutlined,
  MessageOutlined,
  MinusOutlined,
  SendOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { useResolutionInfo } from '@mm/hooks';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { MOBILE_SMALL } from '../../../../../editor/constants/screenSize';
import { ReactComponent as LeadMagnet } from 'assets/lead-generation-magnet.svg';
import styles from './BillingPlans.module.css';
import { uniqueId } from 'lodash';
import { useOpenConditionalDowngradeModal } from '../../../../../common/hooks/use-open-conditional-downgrade-modal';
import {
  AddonSettingsKeyType,
  ADDON_SETTINGS_KEYS,
  EnhancedPlan,
} from '@mm/state/interfaces/subscription';
import { BillingPlanCardProps, useBillingPlans } from './use-billing-plans';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import { BillingPriceTableModal } from '../SubscriptionTabsArea/ActiveSubscriptionTab/BillingPageCard/BillingPriceTableModal/BillingPriceTableModal';
import { BookDemoButton } from '../../../../../common/buttons/BookDemoButton/BookDemoButton';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';

type Props = {
  webTrafficValue?: number;
  landmineOpenId?: number;
  onBillingSuccess?: () => void;
  onBackToParent?: () => void;
  hideFreePlan?: boolean;
  downgradePlansIds?: number[];
};

export const BillingPlans = ({
  webTrafficValue,
  landmineOpenId,
  onBillingSuccess,
  onBackToParent,
  hideFreePlan,
  downgradePlansIds,
}: Props) => {
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const companyId = useSelector(selectActiveCompanyId);

  const [isAnnual, setIsAnnual] = useState(false);
  const [modalProps, setModalProps] = useState<
    { plan?: EnhancedPlan; type: AddonSettingsKeyType } | undefined
  >(undefined);

  const {
    billingPlans,
    activeFacebookPage,
    currentPlan,
    tabId,
    pageId,
    plansToDowngrade,
    canCancelSubscription,
    isAwaitingCancellation,
    isManageSubscriptionsPage,
    getPlanDiscountPrice,
    getCurrentPlan,
  } = useBillingPlans({
    isAnnual,
    onBillingSuccess,
    hideFreePlan,
    landmineOpenId,
    onBackToParent,
  });
  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();
  const { getPlans } = useSubscriptionCRUDActions();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  // Uncomment when implement annual plans
  // useEffect(() => {
  //   if (activeFacebookPage?.subscription) {
  //     setIsAnnual(
  //       !!(activeFacebookPage?.subscription.billingPeriodLength === 'annually'),
  //     );
  //   }
  // }, []);

  const pointIcons = [
    <LeadMagnet
      key="leadMagnet"
      className={classNames(styles.pointIcon, styles.leadMagnetIcon)}
    />,
    <SendOutlined key="send" className={styles.pointIcon} />,
    <UsergroupAddOutlined key="userGroup" className={styles.pointIcon} />,
    <LikeOutlined key="like" className={styles.pointIcon} />,
    <MessageOutlined key="message" className={styles.pointIcon} />,
  ];

  const pricingTypes = [
    ADDON_SETTINGS_KEYS.xrayLeads,
    ADDON_SETTINGS_KEYS.emailSends,
    ADDON_SETTINGS_KEYS.mercuryLeads,
    ADDON_SETTINGS_KEYS.socialSends,
    ADDON_SETTINGS_KEYS.smsSends,
  ];

  const filteredOptions = billingPlans.filter(option => !option.hide);

  const { openConditionalDowngradeModal } = useOpenConditionalDowngradeModal();

  const onCancelSubscription = () => {
    if (activeFacebookPage?.subscription && currentPlan) {
      openConditionalDowngradeModal({
        subscription: activeFacebookPage.subscription,
        plan: currentPlan,
        facebookPage: activeFacebookPage,
        tabId,
      });
    }
  };
  const utm = {
    utmCampaign: 'sqlsetupcall',
    utmMedium: 'billling',
    utmSource: 'inproduct',
  };

  const getOptionParams = (option: BillingPlanCardProps) => {
    const downgrade = plansToDowngrade.find(
      plan =>
        (isAnnual ? option.planCodes?.[1] : option.planCodes?.[0]) ===
        plan.planCode,
    );
    const currentTag = option.planCodes?.includes(currentPlan?.planCode || '');

    const disabled =
      webTrafficValue && option?.threshold
        ? option?.threshold?.[1] < webTrafficValue
        : false;

    const showRecommendedTag =
      !option.title.includes('Free') &&
      webTrafficValue &&
      option?.threshold &&
      option.threshold?.[1] >= webTrafficValue &&
      option.threshold?.[0] <= webTrafficValue;

    const buttonNameAdjusted = currentTag
      ? 'Current Plan'
      : downgrade
      ? 'Downgrade'
      : option.buttonName;

    return {
      downgrade,
      currentTag,
      buttonNameAdjusted,
      disabled,
      showRecommendedTag,
    };
  };

  useEffect(() => {
    if (!companyId) {
      return;
    }
    (async () => {
      if (!plansMapping) {
        await getPlans(companyId);
      }
    })();
  }, [plansMapping, companyId, getPlans]);

  return (
    <div className={styles.container}>
      {/* {!activeFacebookPage?.subscription && (       // on BE we not yet ready to sell annual plans
        <div className={styles.toggleWrapper}>
          <ToggleSwitch
            isOn={isAnnual}
            handleToggle={() => setIsAnnual(!isAnnual)}
            leftSwitchText="Monthly"
            rightSwitchtext="Annually"
            classes={{
              container: styles.toggleContainer,
              trigger: styles.toggleTrigger,
              activeTrigger: styles.toggleTriggerActive,
            }}
          />
          <div className={styles.discountTag}>
            -
            {Math.ceil(
              getPlanDiscountPrice(STARTER_PLAN_CODES[0], STARTER_PLAN_CODES[1])
                .discountPercent || 10,
            ).toFixed(0)}{' '}
            %
          </div>
        </div>
      )} */}
      {canCancelSubscription &&
        (isAwaitingCancellation ? (
          <div className={styles.cancellationInfo}>
            Awaiting cancellation approval
          </div>
        ) : (
          <PrimaryButton
            onClick={onCancelSubscription}
            className={styles.cancelButton}
            color="transparent"
          >
            <CloseOutlined />
            Cancel your plan
          </PrimaryButton>
        ))}

      <div
        className={classNames(styles.content, {
          [styles.contentWhite]: isManageSubscriptionsPage,
          [styles.lessContent]: filteredOptions.length <= 3,
          [styles.contentWithTag]: isFreeTrialPromotionActive,
        })}
      >
        {filteredOptions.map(option => {
          const {
            downgrade,
            currentTag,
            buttonNameAdjusted,
            disabled,
            showRecommendedTag,
          } = getOptionParams(option);
          const { isFreeTrial } = option || {};
          return (
            <div
              key={option.title}
              className={classNames(styles.card, {
                [styles.inactiveCard]: disabled,
                [styles.currentPlanCard]: !!currentTag,
              })}
            >
              {showRecommendedTag && !currentTag && (
                <div className={styles.tag}>Recommended</div>
              )}
              {isFreeTrialPromotionActive && isFreeTrial && (
                <div className={classNames(styles.tag, styles.trialTag)}>
                  Up to 500 leads FREE
                </div>
              )}
              <div
                className={classNames(styles.heading, {
                  [styles.centered]:
                    isAnnual && option.priceDiscount && isMobile,
                })}
              >
                {option.title}
                {isMobile && (
                  <Price
                    option={option}
                    disabled={disabled}
                    isAnnual={isAnnual}
                  />
                )}
              </div>
              <div className={styles.description}>
                {option.header && (
                  <div className={styles.heading}>{option.header}</div>
                )}
                <div className={styles.text}>{option.description}</div>
              </div>
              {!isMobile && (
                <Price
                  option={option}
                  disabled={disabled}
                  isAnnual={isAnnual}
                />
              )}
              <div className={styles.buttonsContainer}>
                <PrimaryButton
                  className={classNames(styles.button, {
                    [styles.secondaryButton]: disabled || downgrade,
                    [styles.disabledButton]: isFreeTrial || currentTag,
                  })}
                  onClick={option.onClick}
                >
                  {buttonNameAdjusted}
                </PrimaryButton>
                {option.title === 'Pro' && (
                  <BookDemoButton
                    button="Schedule Demo"
                    className={styles.link}
                    utm={utm}
                  />
                )}
              </div>
              <div className={styles.points}>
                {option.points?.map((point, index) => {
                  const { plan } = getCurrentPlan(option.planCodes) || {};
                  const type = pricingTypes[index];
                  const hasAddonSettings = plan?.addonSettings?.[type];

                  return (
                    <div
                      className={styles.pointContainer}
                      key={uniqueId(point)}
                    >
                      {point ? pointIcons[index] : null}
                      <div className={styles.pointText}>
                        {point ? (
                          <div>
                            {point}
                            {option.planCodes && hasAddonSettings && (
                              <InfoCircleOutlined
                                className={styles.infoIcon}
                                onClick={() => {
                                  setModalProps({
                                    type,
                                    plan,
                                  });
                                }}
                              />
                            )}
                          </div>
                        ) : (
                          <MinusOutlined />
                        )}

                        {option?.secondaryPoints && (
                          <div
                            className={classNames(styles.smallText, {
                              [styles.inactiveCard]: disabled,
                            })}
                          >
                            {option.secondaryPoints?.[index]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {modalProps && (
        <Overlay className={styles.pricingOverlay}>
          <BillingPriceTableModal
            type={modalProps.type}
            pageId={activeFacebookPage?.id || Number(pageId)}
            plan={modalProps.plan}
            onClose={() => setModalProps(undefined)}
          />
        </Overlay>
      )}
    </div>
  );
};

const Price = ({
  option,
  disabled,
  isAnnual,
}: {
  option: BillingPlanCardProps;
  disabled: boolean;
  isAnnual: boolean;
}) => (
  <div className={classNames(styles.price)}>
    <div className={styles.smallText}>
      {option.pricePeriod ? 'Starts at' : ''}
    </div>
    <div className={styles.priceContainer}>
      <span className={styles.priceValue}>
        {' '}
        {option.price}
        <span className={styles.pricePeriod}>{option.pricePeriod}</span>
      </span>

      {isAnnual && (option?.priceDiscount || 0) > 0 && (
        <span
          className={classNames(styles.discountTag, {
            [styles.inactiveCard]: disabled,
          })}
        >
          Save ${option.priceDiscount?.toFixed(0)}
        </span>
      )}
    </div>
  </div>
);
